import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Twitter, Rss, Mail } from 'lucide-react';

const Footer: React.FC = () => {
  return (
    <footer className="footer">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">

          {/* Column 1: About */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img
                src="/operator-avatar.png"
                alt="The Operator"
                className="w-8 h-8 rounded-lg ring-1 ring-red-500/50"
              />
              <h3 className="text-lg font-bold text-gray-100">AIOFH</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              AI Operator From Hell is satirical fiction celebrating sysadmin culture.
              All stories are made up. All tools are simulated. All fun is real.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/10 border border-teal-500/30 rounded-full">
              <span className="text-teal-500 text-xs font-bold">✅ Ethics & Safety Certified</span>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-sm font-bold text-gray-100 uppercase tracking-wider mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/episodes" className="footer-link">
                  Latest Episodes
                </Link>
              </li>
              <li>
                <Link to="/about" className="footer-link">
                  About The Operator
                </Link>
              </li>
              <li>
                <Link to="/tools" className="footer-link">
                  Tools
                </Link>
              </li>
              <li>
                <Link to="/tutorials" className="footer-link">
                  Tutorials
                </Link>
              </li>
              <li>
                <Link to="/ethics" className="footer-link">
                  Ethics & Disclaimers
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div>
            <h4 className="text-sm font-bold text-gray-100 uppercase tracking-wider mb-4">
              Resources
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="https://owasp.org" target="_blank" rel="noopener noreferrer" className="footer-link">
                  OWASP
                </a>
              </li>
              <li>
                <a href="https://sans.org" target="_blank" rel="noopener noreferrer" className="footer-link">
                  SANS Institute
                </a>
              </li>
              <li>
                <a href="https://nist.gov/cyberframework" target="_blank" rel="noopener noreferrer" className="footer-link">
                  NIST Framework
                </a>
              </li>
              <li>
                <a href="https://hackthebox.com" target="_blank" rel="noopener noreferrer" className="footer-link">
                  HackTheBox
                </a>
              </li>
              <li>
                <a href="https://tryhackme.com" target="_blank" rel="noopener noreferrer" className="footer-link">
                  TryHackMe
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Connect */}
          <div>
            <h4 className="text-sm font-bold text-gray-100 uppercase tracking-wider mb-4">
              Connect
            </h4>
            <div className="space-y-3 mb-6">
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 footer-link"
              >
                <Github className="w-4 h-4" />
                <span className="text-sm">GitHub</span>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 footer-link"
              >
                <Twitter className="w-4 h-4" />
                <span className="text-sm">Twitter</span>
              </a>
              <a
                href="/rss"
                className="flex items-center gap-2 footer-link"
              >
                <Rss className="w-4 h-4" />
                <span className="text-sm">RSS Feed</span>
              </a>
              <a
                href="mailto:contact@aoifh.com"
                className="flex items-center gap-2 footer-link"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm">Contact</span>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 pt-6 pb-6">
          {/* Bottom Row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <div className="text-sm text-gray-500 text-center md:text-left">
              © 2025 AI Operator From Hell. All content is fictional satire.
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-4 text-sm">
              <Link to="/ethics" className="footer-link">
                Ethics
              </Link>
              <span className="text-gray-700">•</span>
              <a href="/privacy" className="footer-link">
                Privacy
              </a>
              <span className="text-gray-700">•</span>
              <a href="/terms" className="footer-link">
                Terms
              </a>
            </div>
          </div>

          {/* Final Disclaimer */}
          <div className="mt-6 pt-6 border-t border-gray-800/50">
            <p className="text-xs text-gray-500 text-center leading-relaxed max-w-4xl mx-auto">
              ⚠️ <strong className="text-gray-400">Disclaimer:</strong> All content on AI Operator From Hell is
              fictional and satirical. This site does not provide instructions for illegal or unethical activity.
              For real-world security learning, visit{' '}
              <a href="https://owasp.org" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400 transition-colors">
                OWASP
              </a>,{' '}
              <a href="https://sans.org" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400 transition-colors">
                SANS
              </a>, and{' '}
              <a href="https://nist.gov/cyberframework" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400 transition-colors">
                NIST
              </a>.
            </p>
          </div>

          {/* The Operator's Signature */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-600 italic font-mono">
              "Documented. Filed. Ready for deployment."
            </p>
            <p className="text-xs text-gray-700 mt-1">
              — The Operator
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
